import React, { useEffect, useState, forwardRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ClipboardList, ExternalLink, FileText, MessageSquare, Package } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import RepairRequestDetails from '../RepairRequest/RepairRequestDetails';
import CreateClientTicketPanel from '../ClientTickets/CreateClientTicketPanel';
import { ShipmentDocumentsModal } from '../Packaging/PackagingDetails';
import styles from './ShippingDetails.module.css';
import commonStyles from '../../styles/common.module.css';

const ShippingDetails = forwardRef(({ batchNumber: propBatchNumber, allProjects = false }, ref) => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const batchNumber = propBatchNumber || query.get('batch_number');
  const isPanel = !!propBatchNumber;

  const [boxes, setBoxes] = useState([]);
  const [shippingRequests, setShippingRequests] = useState([]);
  const [details, setDetails] = useState(null);
  const [referenceStatus, setReferenceStatus] = useState(null);
  const [trackNumber, setTrackNumber] = useState('');
  const [arrivalDate, setArrivalDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [customStatusIntegrations, setCustomStatusIntegrations] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [ticketRequest, setTicketRequest] = useState(null);
  const [shipmentDocuments, setShipmentDocuments] = useState(null);
  const [shipmentDocumentsLoading, setShipmentDocumentsLoading] = useState(false);

  useEffect(() => {
    if (batchNumber) {
      setSelectedRequest(null);
      setTicketRequest(null);
      setShipmentDocuments(null);
      fetchShippingDetails();
    }
  }, [batchNumber]);

  const formatDate = (value) => {
    if (!value) return 'Не указана';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Не указана';
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  useEffect(() => {
    if (details?.customerId && details?.projectId) {
      fetchCustomStatusIntegrations(details.customerId, details.projectId);
    }
  }, [details?.customerId, details?.projectId]);

  const fetchShippingDetails = async () => {
    try {
      setLoading(true);
      const selectedProjectId = allProjects ? null : localStorage.getItem('selected_project_id');
      const params = {
        batch_number: batchNumber,
        view_mode: 'awaiting', // Фильтруем коробки, готовые к отправке
      };
      if (selectedProjectId) {
        params.project_id = selectedProjectId;
      }
      const resp = await api.get('/repairs/boxes/shipping_summary/', {
        params,
      });
      const allBoxes = resp.data.boxes || [];
      if (allBoxes.length === 0) {
        toast.error('Нет коробок с указанным номером партии', { toastId: 'fetch-boxes-empty', autoClose: 3000 });
      }
      setBoxes(allBoxes);
      setShippingRequests(resp.data.requests || []);
      setDetails(resp.data.details || null);
      setReferenceStatus(resp.data.reference_status || null);
    } catch (err) {
      console.error(err);
      const errorMsg = err.response?.data?.error || 'Ошибка загрузки деталей отправки';
      toast.error(errorMsg, { toastId: 'fetch-boxes-error', autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const openShipmentDocuments = async () => {
    if (!batchNumber) return;
    setShipmentDocumentsLoading(true);
    try {
      const { data } = await api.get('/repairs/boxes/shipment_documents/', {
        params: { batch_number: batchNumber },
      });
      setShipmentDocuments(data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Не удалось загрузить документы партии');
    } finally {
      setShipmentDocumentsLoading(false);
    }
  };

  const fetchCustomStatusIntegrations = async (customerId, projectId) => {
    if (!customerId || !projectId) return;
    try {
      const resp = await api.get(
        `/repairs/custom-status-integrations/?customer_id=${customerId}&project_id=${projectId}`
      );
      setCustomStatusIntegrations(resp.data);
    } catch (err) {
      console.error('Ошибка при загрузке пользовательских статусов:', err);
      const errorMsg = err.response?.data?.error || 'Ошибка загрузки пользовательских статусов';
      toast.error(errorMsg, { toastId: 'fetch-custom-status-error', autoClose: 3000 });
    }
  };

  const handleSendShipment = async () => {
    if (!trackNumber) {
      toast.error('Введите трек номер отправки', { toastId: 'send-shipment-no-track', autoClose: 3000 });
      return;
    }
    if (!boxes.length) {
      toast.error('Нет коробок для отправки', { toastId: 'send-shipment-no-boxes', autoClose: 3000 });
      return;
    }
    try {
      const shipments = boxes
        .filter(box => box.has_unshipped_equipment)
        .map(box => ({
          box_id: box.id,
          track_number: trackNumber,
          arrival_date: arrivalDate.toISOString(),
        }));
      if (!shipments.length) {
        toast.error('Все коробки в партии уже отправлены', { toastId: 'send-shipment-all-shipped', autoClose: 3000 });
        return;
      }
      const payload = {
        batch_number: batchNumber,
        shipments: shipments,
      };
      await api.post('/repairs/boxes/send_shipment/', payload);
      toast.success('Отправка успешно завершена', { toastId: 'send-shipment-success', autoClose: 3000 });
      if (!isPanel) {
        navigate('/shipping');
      }
    } catch (err) {
      console.error(err);
      const errorMsg = err.response?.data?.error || 'Ошибка отправки';
      toast.error(errorMsg, { toastId: 'send-shipment-error', autoClose: 3000 });
    }
  };

  const handleCustomStatusTrigger = async (customStatusKey) => {
    try {
      await api.post('/repairs/boxes/trigger_custom_status/', { custom_status_key: customStatusKey, batch_number: batchNumber });
      toast.success('Интеграция успешно запущена', { toastId: 'custom-status-success', autoClose: 3000 });
      if (!isPanel) {
        navigate('/shipping');
      }
    } catch (err) {
      console.error(`Ошибка при запуске интеграции для статуса ${customStatusKey}:`, err);
      const errorMsg = err.response?.data?.error || 'Ошибка запуска интеграции';
      toast.error(errorMsg, { toastId: 'custom-status-error', autoClose: 3000 });
    }
  };

  const selectedProjectId = localStorage.getItem('selected_project_id');

  const customIntegration =
    referenceStatus === 'awaiting_shipping' &&
    details &&
    details.projectId &&
    parseInt(selectedProjectId, 10) === details.projectId
      ? customStatusIntegrations.find(
          integration => integration.previous_status === 'awaiting_shipping'
        )
      : null;

  if (loading) return <div className={commonStyles.loading}><div className={commonStyles.spinner}></div></div>;

  if (selectedRequest) {
    return (
      <div className={`${commonStyles.pageContainer} ${commonStyles.fadeIn}`}>
        <div className={styles.drilldownToolbar}>
          <button
            type="button"
            className={styles.backToShipmentButton}
            onClick={() => setSelectedRequest(null)}
          >
            <ArrowLeft size={18} />
            Назад к отправке {batchNumber}
          </button>
          <div className={styles.drilldownTitle}>
            <ClipboardList size={20} />
            <span>{selectedRequest.request_number}</span>
          </div>
        </div>
        <div className={styles.requestDetailWrapper}>
          <RepairRequestDetails repairRequestId={selectedRequest.id} />
        </div>
      </div>
    );
  }

  return (
    <div className={`${commonStyles.pageContainer} ${commonStyles.fadeIn}`}>
      <div className={commonStyles.pageHeader}>
        <h1 className={commonStyles.pageTitle}>Детали отправки</h1>
      </div>
      {details && (
        <div className={styles.detailsContainer}>
          <p><strong>Номер партии:</strong> {batchNumber}</p>
          <p><strong>Территория, Город:</strong> {details.territoryCity}</p>
          <p><strong>Полный адрес:</strong> {details.fullAddress}</p>
          <p><strong>Заказчик:</strong> {details.customerName}</p>
          <p><strong>Количество коробок:</strong> {details.count}</p>
          <button type="button" className={styles.documentsButton} onClick={openShipmentDocuments}>
            <FileText size={18} /> Бланки коробок и акт ПП
          </button>
        </div>
      )}
      <div className={styles.formContainer}>
        <div className={styles.inputRow}>
          <div className={styles.inputGroup}>
            <label>Трек номер отправки:</label>
            <input
              type="text"
              value={trackNumber}
              onChange={(e) => setTrackNumber(e.target.value)}
              placeholder="Введите трек номер"
            />
          </div>
          <div className={styles.inputGroup}>
            <label>Дата прибытия:</label>
            <DatePicker
              selected={arrivalDate}
              onChange={(date) => setArrivalDate(date)}
              dateFormat="dd.MM.yyyy"
            />
          </div>
        </div>
        <div className={styles.buttonContainer}>
          {customIntegration ? (
            <button onClick={() => handleCustomStatusTrigger(customIntegration.status_key)}>
              {customIntegration.display_name}
            </button>
          ) : (
            <button onClick={handleSendShipment}>Выслать</button>
          )}
        </div>
      </div>
      <div className={styles.requestsSection}>
        <div className={styles.sectionHeader}>
          <div>
            <h2 className={styles.sectionTitle}>
              <ClipboardList size={20} />
              Заявки в отправке
            </h2>
            <p className={styles.sectionDescription}>
              Можно открыть заявку, посмотреть дату, адрес и оборудование, затем вернуться к партии.
            </p>
          </div>
          <span className={styles.requestCount}>{shippingRequests.length}</span>
        </div>

        {shippingRequests.length > 0 ? (
          <div className={styles.requestGrid}>
            {shippingRequests.map((request) => (
              <div key={request.id} className={styles.requestCard}>
                <div className={styles.requestCardHeader}>
                  <div>
                    <div className={styles.requestNumber}>{request.request_number}</div>
                    <div className={styles.requestCustomer}>{request.customer?.name || 'Заказчик не указан'}</div>
                  </div>
                  <div className={styles.requestActions}>
                    <button
                      type="button"
                      className={styles.ticketRequestButton}
                      onClick={() => setTicketRequest(request)}
                    >
                      <MessageSquare size={16} />
                      Тикет
                    </button>
                    <button
                      type="button"
                      className={styles.openRequestButton}
                      onClick={() => setSelectedRequest(request)}
                    >
                      <ExternalLink size={16} />
                      Открыть заявку
                    </button>
                  </div>
                </div>

                <div className={styles.requestMetaGrid}>
                  <div className={styles.requestMetaItem}>
                    <span>Дата заявки</span>
                    <strong>{formatDate(request.created_at)}</strong>
                  </div>
                  <div className={styles.requestMetaItem}>
                    <span>Статус</span>
                    <strong>{request.status_display || request.status || 'Не указан'}</strong>
                  </div>
                  <div className={styles.requestMetaItem}>
                    <span>Сервисный центр</span>
                    <strong>{request.service_center?.name || 'Не указан'}</strong>
                  </div>
                  <div className={styles.requestMetaItem}>
                    <span>Оборудование в партии</span>
                    <strong>{request.equipment_count} из {request.total_equipment || request.equipment_count}</strong>
                  </div>
                </div>

                <div className={styles.equipmentPreview}>
                  <div className={styles.equipmentPreviewTitle}>
                    <Package size={16} />
                    Оборудование
                  </div>
                  <ul>
                    {request.equipment_preview.map((item) => (
                      <li key={item.id}>
                        <span>{item.serial_number || 'Без серийного номера'}</span>
                        <small>
                          {item.equipment_model || 'Модель не указана'}
                          {item.location_status_display ? ` · ${item.location_status_display}` : ''}
                        </small>
                      </li>
                    ))}
                  </ul>
                  {request.equipment_count > 4 && (
                    <div className={styles.moreEquipment}>
                      Ещё {request.equipment_count - 4} ед. в заявке
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.emptyRequests}>
            В этой партии не найдены связанные заявки.
          </div>
        )}
      </div>
      <CreateClientTicketPanel
        isOpen={Boolean(ticketRequest)}
        repairRequest={ticketRequest}
        onClose={() => setTicketRequest(null)}
      />
      <ShipmentDocumentsModal
        data={shipmentDocuments}
        loading={shipmentDocumentsLoading}
        onClose={() => setShipmentDocuments(null)}
      />
    </div>
  );
});

ShippingDetails.displayName = 'ShippingDetails';

export default ShippingDetails;
